// Infrastructure: error monitoring (must be first import)
import "./infrastructure/sentry";
import "./index.css";
// src/main.tsx
// CRITICAL: Check env before Firebase initialization to prevent auth/invalid-api-key errors
import React from "react";
import ReactDOM from "react-dom/client";
import { checkRequiredEnv } from "./bootstrap/requireEnv";
import { SetupNeededPage } from "./pages/SetupNeededPage";

// Stale chunk / failed module import after deploy — catch dynamic AND static import
// failures and reload to get fresh assets.
//
// Patterns covered:
//   - Standard Vite chunk load errors (ChunkLoadError, dynamically imported module)
//   - iOS Safari static module import failure ("Importing a module script failed")
//   - Firefox/Chrome network-level module failures
//
// The service worker intentionally does NOT intercept JS/CSS assets (see public/sw.js),
// so a genuine network failure on a chunk reaches the browser natively and surfaces here.
if (typeof window !== "undefined") {
  const CHUNK_RECOVERY_KEY = "cg_chunk_recovery_v1";

  const reloadWithBust = () => {
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("__cg_reload", String(Date.now()));
    window.location.replace(nextUrl.toString());
  };

  const recoverFromChunkError = () => {
    try {
      if (window.sessionStorage.getItem(CHUNK_RECOVERY_KEY) === "1") {
        // Avoid reload loops; let normal fallback UI render on second failure.
        window.sessionStorage.removeItem(CHUNK_RECOVERY_KEY);
        return;
      }
      window.sessionStorage.setItem(CHUNK_RECOVERY_KEY, "1");
    } catch {
      // If sessionStorage is unavailable, still attempt recovery.
    }

    const hasSW = "serviceWorker" in navigator;
    const hasCaches = "caches" in window;

    if (!hasSW && !hasCaches) {
      reloadWithBust();
      return;
    }

    const clearCachesPromise = hasCaches
      ? caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))).then(() => undefined))
      : Promise.resolve();
    const unregisterSwPromise = hasSW
      ? navigator.serviceWorker.getRegistrations().then((regs) => Promise.all(regs.map((reg) => reg.unregister())).then(() => undefined))
      : Promise.resolve();

    void Promise.all([clearCachesPromise, unregisterSwPromise]).finally(reloadWithBust);
  };

  window.addEventListener("unhandledrejection", (event) => {
    const msg = event.reason?.message ?? "";
    const isChunkError =
      msg.includes("dynamically imported module") ||
      msg.includes("not a valid JavaScript MIME type") ||
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("ChunkLoadError") ||
      // iOS Safari static vendor chunk failure (e.g. vendor-ptEE1OsD.js after deploy)
      msg.includes("Importing a module script failed") ||
      msg.includes("error loading dynamically imported module");
    if (isChunkError) {
      event.preventDefault();
      recoverFromChunkError();
    }
  });

  // Also catch synchronous TypeError from static module imports on iOS Safari.
  // unhandledrejection only fires for promise rejections; a synchronous module
  // parse error surfaces as a window.onerror instead.
  const _originalOnerror = window.onerror;
  window.onerror = (message, _source, _lineno, _colno, error) => {
    const msg = typeof message === "string" ? message : (error?.message ?? "");
    if (msg.includes("Importing a module script failed")) {
      recoverFromChunkError();
      return true; // suppress further handling
    }
    if (_originalOnerror) return _originalOnerror(message, _source, _lineno, _colno, error);
    return false;
  };
}

// Check environment variables first (before any Firebase imports)
const envStatus = checkRequiredEnv();

// If env is missing, show setup screen immediately (Firebase never initializes)
if (!envStatus.ok) {
  const root = ReactDOM.createRoot(document.getElementById("root")!);
  root.render(
    <React.StrictMode>
      <SetupNeededPage missing={envStatus.missing} />
    </React.StrictMode>
  );
} else {
  // Env is OK - safe to initialize Firebase and render app
  // Use dynamic import to ensure Firebase only loads when env is present
  Promise.all([
    import("@/firebase/firebase"),
    import("./App")
  ]).then(([_, { default: App }]) => {
    try {
      window.sessionStorage.removeItem("cg_chunk_recovery_v1");
    } catch {
      // no-op
    }
    const root = ReactDOM.createRoot(document.getElementById("root")!);
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }).catch((error) => {
    // Fallback if imports fail
    console.error("[main] Failed to load app:", error);
    const root = ReactDOM.createRoot(document.getElementById("root")!);
    root.render(
      <React.StrictMode>
        <SetupNeededPage missing={["We couldn't load the application"]} />
      </React.StrictMode>
    );
  });
}
